import React, {Component, ReactNode} from 'react'
import {RESERVATION_STATUS} from '../../../CodeMaster'
import {Nullable} from '../../../app/TypeHelpers'
import Time from '../../../dateHelpers/Time'
import EventEmitterExResident from '../../shared/pageBaseComponents/EventEmitterExResident'
import ReserveAvailabilityDuration from '../ReserveAvailabilityDuration'
import equal from 'fast-deep-equal'
import ReservationsRepository from '../ReservationsRepository'
import DatePickerForResident from '../components/DatePickerForResident'
import {convertToMoment} from '../helpers/helpers'
import {NatAsyncCallFunction} from '../../../__NoAutomatedTests/common/util/Natutil'
import {formatDateLocal} from '../../../formatters/formatMoment'
import {Box} from '@mui/material'
import {DisplayItem, DisplayItemLabelDivNoMargin} from '../../../styledComponents/DisplayItem'
import {
    BlockSpacer,
    BlockSpacerPcOnly,
    BlockSpacerSpOnly,
    GridContainer,
    GridItem,
} from '../../../styledComponents/ResponsiveConvenience'
import {ReservationFlexContainer, StyledReservationDetail} from '../components/CommonStyle'
import {fromDateAndTimeToMoment} from '../../../dateHelpers/MomentHelper'
import moment from 'moment'
import styled from 'styled-components'
import SimpleReservationStatusTag from './SimpleReservationStatusTag'
import {Colors, FontWeights} from '../../../styledComponents/Constants'
import {RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {PlainLinkStyledButton} from '../../../styledComponents/Button'
import ResponsiveDialog from '../../shared/ResponsiveDialog'
import RFRSReservationStatusDetailForDurationComponent
    from '../ResidentFacilityReservationStatusComponents/RFRSReservationStatusDetailForDurationComponent'
import {NavigateFunction} from 'react-router-dom'

interface TimePeriod {
    startTime: Time
    endTime: Time
}

interface MyReservation {
    id: number
    status: RESERVATION_STATUS,
    startDate: string
    startTime: Time
    endDate: string
    endTime: Time
    ownerName: Nullable<string>
    title: string
}

interface Props {
    targetDate: string

    condominiumCd: string
    facilityId: number
    facilityName: string
    reservationId: Nullable<number>
    reservationsRepo: ReservationsRepository
    eventEmitter: EventEmitterExResident

    onChangeDate: (date: string) => void
    onChangeReserveAvailabilityDuration: (data: ReserveAvailabilityDuration) => void

    navigate: NavigateFunction
    currentDateTime: moment.Moment
    holidays: string[]
}

interface State {
    reserveAvailableTimePeriods: TimePeriod[]
    reserveAvailabilityData: Nullable<ReserveAvailabilityDuration>
    myReservations: MyReservation[]
    openDetailStatusDialog: boolean
}

class RRFFReserveAvailabilityViewDurationFacility extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            reserveAvailableTimePeriods: [],
            reserveAvailabilityData: null,
            myReservations: [],
            openDetailStatusDialog: false,
        }

        this.handleClickFacilityReserveStatusDetail = this.handleClickFacilityReserveStatusDetail.bind(this)
        this.handleCloseDetailStatusForDurationDialog = this.handleCloseDetailStatusForDurationDialog.bind(this)

    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return (nextProps.targetDate !== this.props.targetDate) || (!equal(nextState, this.state))
    }

    async componentDidMount(): Promise<void> {
        await this.updateStateAndCallback()
    }

    async componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): Promise<void> {
        if (prevProps.targetDate === this.props.targetDate) {
            return
        }
        await this.updateStateAndCallback()
    }

    async updateStateAndCallback(): Promise<void> {
        const reserveAvailabilityData = await this.getReserveAvailabilityData()
        if (reserveAvailabilityData === null) {
            return
        }

        this.setState({
            reserveAvailableTimePeriods: reserveAvailabilityData.reserveAvailableList.map(item => {
                return {
                    startTime: Time.fromString(item.startTime),
                    endTime: Time.fromString(item.endTime),
                }
            }),
            myReservations: reserveAvailabilityData.currentReserves.map(item => {
                const startDateTime = Time.fromDateTimeString(item.startDateTime)
                const endDateTime = Time.fromDateTimeString(item.endDateTime)
                return {
                    id: item.id,
                    status: item.status,
                    startDate: startDateTime.date,
                    startTime: startDateTime.time,
                    endDate: endDateTime.date,
                    endTime: endDateTime.time,
                    ownerName: item.ownerName,
                    title: item.title,
                }
            }).sort((a, b) => {
                const startMomentA = this.toMoments(a).start
                const startMomentB = this.toMoments(b).start
                return startMomentA.valueOf() - startMomentB.valueOf()
            })
            ,
            reserveAvailabilityData: reserveAvailabilityData
        }, () => {
            this.props.onChangeReserveAvailabilityDuration(reserveAvailabilityData)
        })
    }

    private toMoments(reservation: MyReservation): { start: moment.Moment, end: moment.Moment } {
        return {
            start: fromDateAndTimeToMoment(
                reservation.startDate,
                `${reservation.startTime.hours}`,
                `${reservation.startTime.minutes}`,
            ),
            end: fromDateAndTimeToMoment(
                reservation.endDate,
                `${reservation.endTime.hours}`,
                `${reservation.endTime.minutes}`,
            ),
        }
    }

    async getReserveAvailabilityData(): Promise<Nullable<ReserveAvailabilityDuration>> {
        try {
            await this.props.eventEmitter.showLoading()
            return await NatAsyncCallFunction(this.getReserveStatusForDurationRental.bind(this), this.props.navigate)
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    private async getReserveStatusForDurationRental(): Promise<ReserveAvailabilityDuration> {
        const {reservationsRepo, condominiumCd, facilityId, reservationId, targetDate} = this.props
        const startMoment = convertToMoment(targetDate, null)
        const endMoment = convertToMoment(targetDate, null)

        return await reservationsRepo.getReserveStatusForDurationRental(
            condominiumCd,
            facilityId,
            reservationId,
            formatDateLocal(startMoment),
            '00:00',
            formatDateLocal(endMoment),
            '24:00',
            true,
        )
    }

    handleClickFacilityReserveStatusDetail(): void {
        this.setState({
            openDetailStatusDialog: true,
        })
    }

    handleCloseDetailStatusForDurationDialog(): void {
        this.setState({
            openDetailStatusDialog: false,
        })
    }

    renderDurationDetailDialog(): ReactNode {
        const {facilityId, facilityName, reservationId, condominiumCd, navigate, targetDate} = this.props
        const {reserveAvailabilityData, openDetailStatusDialog} = this.state
        if (!reserveAvailabilityData) {
            return null
        }

        return (<>
            <ResponsiveDialog
                id={'pp-dialog'}
                open={openDetailStatusDialog}
                transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                fullWidth
            >
                <RFRSReservationStatusDetailForDurationComponent
                    condominiumCd={condominiumCd}
                    facilityId={facilityId}
                    facilityName={facilityName}
                    reservationId={reservationId}
                    searchDate={moment(targetDate)}
                    reserveAvailabilityDuration={reserveAvailabilityData}
                    navigate={navigate}
                    onCancel={this.handleCloseDetailStatusForDurationDialog}>
                </RFRSReservationStatusDetailForDurationComponent>
            </ResponsiveDialog>
        </>)
    }

    render(): ReactNode {
        return (<>
            {this.renderDurationDetailDialog()}
            <DisplayReservationStatus>
                <RowFlexContainer>
                    <DisplayReservationStatusTitle>
                        空き時間
                    </DisplayReservationStatusTitle>
                    <FacilityReserveStatusDetailButton
                        onClick={this.handleClickFacilityReserveStatusDetail}
                    >
                        予約状況の詳細を見る
                    </FacilityReserveStatusDetailButton>
                </RowFlexContainer>
                <BlockSpacer height={'10px'}/>
                <GridContainer alignItems={'flex-start'}>
                    <GridItem over={12}>
                        <DatePickerForResident
                            date={this.props.targetDate}
                            selectableStartDate={this.props.currentDateTime}
                            onChange={this.props.onChangeDate}
                            holidays={this.props.holidays}
                        />
                    </GridItem>

                    <GridItem over={4}>
                        {
                            this.state.reserveAvailableTimePeriods.length > 0
                                ? <>{
                                    this.state.reserveAvailableTimePeriods
                                        .map((timeZone: TimePeriod, index: number) => {
                                            return (
                                                <Box mt={'10px'} ml={'15px'} key={`${index}`} lineHeight={1}>
                                                    {formatTimePeriod(timeZone.startTime, timeZone.endTime)}
                                                </Box>
                                            )
                                        })
                                }</>
                                : <NoFreeTime>空き時間はありません</NoFreeTime>
                        }
                    </GridItem>
                    <GridItem over={8}>
                        <BlockSpacerPcOnly height={'15px'}/>
                        <BlockSpacerSpOnly height={'20px'}/>
                        {
                            this.state.myReservations.length > 0 &&
                            <>
                                <DisplayItemLabelDivNoMargin>あなたの予約</DisplayItemLabelDivNoMargin>
                                {
                                    this.state.myReservations.map((myReservation: MyReservation, index: number) => {
                                        const editMode = (this.props.reservationId === myReservation.id)
                                        const {start, end} = this.toMoments(myReservation)
                                        return (<div key={`${index}`}>
                                            <BlockSpacer height={index === 0 ? '5px' : '15px'}/>
                                            <ReservationFlexContainer>
                                                <SimpleReservationStatusTag editMode={editMode}>
                                                    {myReservation.status.label}
                                                </SimpleReservationStatusTag>
                                                <Box ml={'15px'}>
                                                    <StyledReservationDetail>
                                                        {
                                                            formatTimePeriodWithDate(
                                                                start, end,
                                                                myReservation.startTime,
                                                                myReservation.endTime)
                                                        }
                                                    </StyledReservationDetail>
                                                    {
                                                        (myReservation.ownerName !== null) &&
                                                        <StyledReservationDetail>
                                                            {myReservation.ownerName}さんが予約
                                                        </StyledReservationDetail>
                                                    }
                                                    {
                                                        (myReservation.title && myReservation.title !== '') &&
                                                        <StyledReservationDetail>
                                                            {myReservation.title}
                                                        </StyledReservationDetail>
                                                    }
                                                </Box>
                                            </ReservationFlexContainer>
                                        </div>)
                                    })
                                }
                            </>
                        }
                    </GridItem>
                </GridContainer>
            </DisplayReservationStatus>
        </>)
    }
}

export const DisplayReservationStatus = styled(DisplayItem)`
  padding: 1.5rem 1.5rem 2rem;
`

export const DisplayReservationStatusTitle = styled.div`
  font-weight: ${FontWeights.Bold};
  line-height: 1.5;
  color: ${Colors.AccentDark};
`

export const FacilityReserveStatusDetailButton = styled(PlainLinkStyledButton)`
  line-height: 1.5;
`

const toDateString = (target: moment.Moment): string => {
    return target.format('M月D日(ddd)')
}

function formatTimePeriod(startTime: Time, endTime: Time): string {
    return `${startTime.toString()}　〜　${endTime.toString()}`
}

function formatTimePeriodWithDate(
    startMoment: moment.Moment,
    endMoment: moment.Moment,
    startTime: Time,
    endTime: Time,
): string {
    const startDateString = toDateString(startMoment)
    const endDateString = toDateString(endMoment)

    const isSame = (startDateString === endDateString)
    return `${startDateString} ${startTime.toString()} 〜 ${isSame ? '' : `${endDateString} `}${endTime.toString()}`
}

export default RRFFReserveAvailabilityViewDurationFacility

const NoFreeTime = styled.div`
  color: #FF0000;
`
